import { Tabs } from 'antd';
import { useNavigate } from 'react-router';
import { DRIVER_TAB_LABELS, driverTabPath, type DriverTab } from '../drivers.paths';
import type { DriverDetail } from '../drivers.api';
import { useDriverDetail } from '../hooks/use-driver-detail';
import { useDriverPermissions } from '../hooks/use-driver-permissions';
import { DriverAvailabilityPanel } from './DriverAvailabilityPanel';
import { DriverBalancePanel } from './DriverBalancePanel';
import { DriverConsentPanel } from './DriverConsentPanel';
import { DriverDetailsForm } from './DriverDetailsForm';
import { DriverDevicesTab } from './DriverDevicesTab';
import { DriverDocuments } from './DriverDocuments';
import { DriverHistoryTab } from './DriverHistoryTab';
import { DriverLedgerTab } from './DriverLedgerTab';

type DriverDetailActions = Pick<ReturnType<typeof useDriverDetail>, 'reload' | 'updateError' | 'saving' | 'update' | 'uploadDocument'>;

interface DriverDetailTabsProps extends DriverDetailActions {
  driver: DriverDetail;
  tab: DriverTab;
}

/** The driver's tabs; the ledger and devices tabs exist only for roles that may see them. */
export function DriverDetailTabs({ driver, tab, reload, updateError, saving, update, uploadDocument }: DriverDetailTabsProps) {
  const navigate = useNavigate();
  const { canManage, canManageDevices, canViewLedger } = useDriverPermissions();
  return (
    <Tabs
      activeKey={tab}
      onChange={(key) => navigate(driverTabPath(driver.id, key as DriverTab))}
      items={[
        {
          key: 'details',
          label: DRIVER_TAB_LABELS.details,
          children: (
            <div className="flex flex-col gap-8">
              <DriverAvailabilityPanel availability={driver.availability} />
              <DriverConsentPanel consent={driver.locationConsent} />
              <DriverDetailsForm driver={driver} canManage={canManage} error={updateError} saving={saving} onSave={update} />
              <DriverBalanceAndScore balance={driver.advanceBalance} />
            </div>
          ),
        },
        {
          key: 'documents',
          label: DRIVER_TAB_LABELS.documents,
          children: <DriverDocuments driverId={driver.id} documents={driver.documents} canManage={canManage} onUpload={uploadDocument} />,
        },
        ...(canViewLedger ? [{ key: 'ledger', label: DRIVER_TAB_LABELS.ledger, children: <DriverLedgerTab driverId={driver.id} onRecorded={reload} /> }] : []),
        ...(canManageDevices ? [{ key: 'devices', label: DRIVER_TAB_LABELS.devices, children: <DriverDevicesTab driverId={driver.id} /> }] : []),
        { key: 'history', label: DRIVER_TAB_LABELS.history, children: <DriverHistoryTab driverId={driver.id} /> },
      ]}
    />
  );
}

/** The advance balance comes from the driver ledger; the behaviour score (M5) isn't built yet — shown as unavailable rather than a guessed shape. */
function DriverBalanceAndScore({ balance }: { balance: DriverDetail['advanceBalance'] }) {
  return (
    <section className="grid grid-cols-2 gap-3">
      <DriverBalancePanel balance={balance} />
      <div className="rounded-lg border border-line bg-surface px-4 py-3">
        <p className="text-sm font-medium text-fg">Behaviour score</p>
        <p className="text-sm text-muted">Not yet available</p>
      </div>
    </section>
  );
}
